export const SIDE_MISSION_EXECUTION_MODE = 'parallel_detour';
export const SIDE_MISSION_RELATIONSHIP_TYPE = 'detour_for';

type SideMissionCustomFields = {
  appointment_execution_mode?: string | null;
  primary_appointment_id?: string | null;
  primary_group_id?: string | null;
  primary_group_number?: string | null;
  intentional_overlap?: boolean | null;
  allowed_overlap_with_appointment_id?: string | null;
  detour_reason?: string | null;
  return_to_primary_required?: boolean | null;
  [key: string]: unknown;
};

type SideMissionLike = {
  id?: string | number | null;
  title?: string | null;
  custom_fields?: SideMissionCustomFields | null;
};

function normalizeString(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }

  return null;
}

function getCustomFields(item: SideMissionLike | null | undefined): SideMissionCustomFields {
  return item?.custom_fields && typeof item.custom_fields === 'object'
    ? item.custom_fields
    : {};
}

export function isSideMission(item: SideMissionLike | null | undefined): boolean {
  return getCustomFields(item).appointment_execution_mode === SIDE_MISSION_EXECUTION_MODE;
}

export function getPrimaryAppointmentId(item: SideMissionLike | null | undefined): string | null {
  return normalizeString(getCustomFields(item).primary_appointment_id);
}

export function getAllowedOverlapAppointmentId(item: SideMissionLike | null | undefined): string | null {
  const customFields = getCustomFields(item);
  return normalizeString(customFields.allowed_overlap_with_appointment_id)
    || normalizeString(customFields.primary_appointment_id);
}

export function appointmentsAllowIntentionalOverlap(
  first: SideMissionLike | null | undefined,
  second: SideMissionLike | null | undefined,
): boolean {
  const firstId = normalizeString(first?.id);
  const secondId = normalizeString(second?.id);

  if (!firstId || !secondId || firstId === secondId) {
    return false;
  }

  const firstCustomFields = getCustomFields(first);
  const secondCustomFields = getCustomFields(second);
  const firstAllows = getAllowedOverlapAppointmentId(first);
  const secondAllows = getAllowedOverlapAppointmentId(second);
  const firstIntentional = firstCustomFields.intentional_overlap !== false;
  const secondIntentional = secondCustomFields.intentional_overlap !== false;

  if (firstAllows && firstAllows === secondId && firstIntentional) {
    return true;
  }

  if (secondAllows && secondAllows === firstId && secondIntentional) {
    return true;
  }

  return false;
}

export function buildDefaultSideMissionTitle(sourceTitle?: string | null, assigneeName?: string | null): string {
  const normalizedSourceTitle = normalizeString(sourceTitle);
  const normalizedAssigneeName = normalizeString(assigneeName);

  if (normalizedSourceTitle) {
    return `Side mission — ${normalizedSourceTitle}`;
  }

  if (normalizedAssigneeName) {
    return `Side mission — ${normalizedAssigneeName}`;
  }

  return 'Side mission';
}
